import { AxiosError } from 'axios';
import { authHeader, authUploadService } from 'services';
import {
  IRepresentant,
  IRepresentantFile,
  IRepresentantPartnersResult,
  IRepresentantResult,
} from 'types/Representant';
import { IPaginatedRequest } from 'types/Util';
import { getUserToken } from 'util/auth';

import { Api } from './api/axios.service';

const FILE_DOWNLOAD_URL = `${process.env.REACT_APP_BASE_API_URL}/api/representative`;

/**
 * List representants
 *
 * @param {*} {
 *   page = 1,
 *   limit = 10,
 * }
 * @return {*}  {Promise<IRepresentantResult>}
 */
const findAllRepresentative = async ({
  page = 1,
  limit = 10,
}: IPaginatedRequest): Promise<IRepresentantResult> => {
  try {
    const { data } = await Api.get(
      `/representative?page=${page}&limit=${limit}`,
      {
        headers: authHeader(),
      }
    );

    return data;
  } catch (reason) {
    const { message } = reason as Error;

    throw new Error(message || 'Não foi possível listar os represetantes');
  }
};

/**
 * Find a representative by id
 *
 * @param {string} representativeId
 * @return {*}  {Promise<IRepresentant>}
 */
const findById = async (representativeId: string): Promise<IRepresentant> => {
  try {
    const { data } = await Api.get(`/representative/${representativeId}`, {
      headers: authHeader(),
    });

    return data;
  } catch (reason) {
    const { message } = reason as Error;

    throw new Error(message || 'Não foi possível buscar os represetante');
  }
};

/**
 * Delete representant by id
 *
 * @param {string} [representantId]
 * @return {*}  {Promise<any>}
 */
const deleteRepresentantById = async (
  representantId?: string
): Promise<any> => {
  try {
    await Api.delete(`/representative/${representantId}`, {
      headers: authHeader(),
    });
  } catch (reason) {
    const { message } = reason as Error;

    throw new Error(message || 'Não foi possível deletar o representante');
  }
};

/**
 * List representative documents
 *
 * @param {(string | undefined)} representativeId
 * @param {string} documentId
 * @return {*}
 */
const getDocument = (representativeId?: string, documentId?: string) => {
  return `${FILE_DOWNLOAD_URL}/downloadDocument/${representativeId}/?access_token=Bearer ${
    getUserToken().token
  }&documentId=${documentId}`;
};

/**
 * Register or update a representative
 *
 * @param {IRepresentant} representative
 * @return {*}  {Promise<void>}
 */
const save = async (representative: IRepresentant): Promise<void> => {
  const formData = new FormData();

  if (!representative.cnpj) {
    representative.cnpj = null;
  }

  representative.documents
    ?.filter((doc: IRepresentantFile) => Boolean(doc.file))
    ?.forEach((document: IRepresentantFile) => {
      formData.append('documentsFiles', document.file as File);
    });

  representative.documents = representative?.documents?.filter(
    (doc: IRepresentantFile) => !doc.file
  );

  formData.append('representative', JSON.stringify(representative));

  try {
    /* If has id should update */
    if (representative._id) {
      await Api.put(`/representative/${representative._id}`, formData, {
        headers: authUploadService(),
      });

      return;
    }

    await Api.post('/representative', formData, {
      headers: authUploadService(),
    });
  } catch (reason) {
    const error = (reason as AxiosError<{ error: string }>).response?.data
      ?.error;

    let message = error;

    if (typeof error !== 'string') {
      if (representative._id) {
        message = 'Não foi possível atualizar o representante';
      } else {
        message = 'Não foi possível cadastrar o representante';
      }
    }

    throw new Error(message);
  }
};

/**
 * Find partners converted by representative
 *
 * @param {(IPaginatedRequest & {
 *   representativeId: string;
 * })} {
 *   page = 1,
 *   limit = 3,
 *   representativeId,
 * }
 * @return {*}  {Promise<IRepresentantPartnersResult>}
 */
const findConvertedPartners = async ({
  page = 1,
  limit = 3,
  representativeId,
}: IPaginatedRequest & {
  representativeId: string;
}): Promise<IRepresentantPartnersResult> => {
  try {
    const { data } = await Api.get(
      `/representative/findConvertedPartners/${representativeId}/?page=${page}&limit=${limit}`,
      {
        headers: authHeader(),
      }
    );

    return data;
  } catch (reason) {
    const { message } = reason as Error;

    throw new Error(
      message ||
        'Não foi possível listar os parceiros convertidos pelo represetante'
    );
  }
};

export const RepresentativeService = {
  findAllRepresentative,
  deleteRepresentantById,
  getDocument,
  save,
  findById,
  findConvertedPartners,
};
